import React, { useState, useEffect } from 'react';
import { FaUser, FaCoins, FaTrophy, FaClock, FaCheckCircle, FaCircle, FaArrowLeft, FaGamepad } from 'react-icons/fa';
import { apiService } from '../services/apiService';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

interface GameSession {
  id: number;
  challengeId: number;
  gameId: number;
  gameName: string;
  gameType: string;
  player1Id: number;
  player1Username: string;
  player1Avatar?: string;
  player1Ready: boolean;
  player2Id: number;
  player2Username: string;
  player2Avatar?: string;
  player2Ready: boolean;
  status: string;
  multiSigWalletId?: number;
  platformFee: number;
  payoutAmount: number;
  createdAt: string;
}

const GameLobby: React.FC = () => {
  const navigate = useNavigate();
  // Accept challengeId from URL (route: /p2p-game/lobby/:challengeId)
  const { challengeId } = useParams<{ challengeId: string }>();
  const [session, setSession] = useState<GameSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [currentUserId, setCurrentUserId] = useState<number | null>(null);

  useEffect(() => {
    fetchSession();
    fetchCurrentUser();
    
    // Poll for updates every 2 seconds
    const interval = setInterval(fetchSession, 2000);
    return () => clearInterval(interval);
  }, [challengeId]);

  useEffect(() => {
    // Check if both players are ready
    if (session && session.player1Ready && session.player2Ready && session.status === 'WAITING') {
      // Start countdown
      startCountdown();
    }
  }, [session]);

  const fetchCurrentUser = () => {
    // This would normally fetch from auth context
    // For now, we'll try to determine from the session
    const storedUserId = localStorage.getItem('userId');
    if (storedUserId) {
      setCurrentUserId(parseInt(storedUserId));
    }
  };

  const fetchSession = async () => {
    if (!challengeId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Fetch session by challengeId
      const response = await apiService.get(`/api/p2p-games/sessions/by-challenge/${challengeId}`);
      if (response.success && response.data) {
        setSession(response.data);
        
        // Check if current user is ready
        if (currentUserId) {
          if (response.data.player1Id === currentUserId) {
            setIsReady(response.data.player1Ready);
          } else if (response.data.player2Id === currentUserId) {
            setIsReady(response.data.player2Ready);
          }
        }
        
        // If game has started, navigate to game board (use session.id from response)
        if (response.data.status === 'IN_PROGRESS') {
          navigate(`/p2p-game/play/${response.data.id}`);
        }
      } else {
        setError('Failed to load game session');
      }
    } catch (err: any) {
      console.error('Error fetching session:', err);
      setError(err.message || 'Failed to load game session');
    } finally {
      setLoading(false);
    }
  };

  const handleReady = async () => {
    if (!session?.id) return;
    
    try {
      const response = await apiService.post(`/api/p2p-games/sessions/${session.id}/ready`, {});
      if (response.success) {
        setIsReady(true);
        toast.success('You are ready!');
        fetchSession();
      } else {
        toast.error('Failed to mark as ready');
      }
    } catch (err: any) {
      console.error('Error marking ready:', err);
      toast.error(err.message || 'Failed to mark as ready');
    }
  };

  const startCountdown = () => {
    setCountdown(3);
    const interval = setInterval(() => {
      setCountdown(prev => {
        if (prev === null || prev <= 1) {
          clearInterval(interval);
          // Game should start automatically via polling
          return null;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleCancel = async () => {
    if (!session?.id) return;
    
    if (!window.confirm('Are you sure you want to leave? Your stake may be forfeited.')) {
      return;
    }
    
    try {
      const response = await apiService.post(`/api/p2p-games/sessions/${session.id}/cancel`, {});
      if (response.success) {
        toast.success('Game cancelled');
        navigate('/p2p-games/challenges');
      } else {
        toast.error('Failed to cancel game');
      }
    } catch (err: any) {
      console.error('Error cancelling game:', err);
      toast.error(err.message || 'Failed to cancel game');
    }
  };

  if (loading && !session) {
    return (
      <div style={{ maxWidth: 1000, margin: '0 auto', padding: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 60 }}>
          <div style={{
            width: 50,
            height: 50,
            border: '4px solid #e2e8f0',
            borderTop: '4px solid #667eea',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
        </div>
      </div>
    );
  }

  if (error || !session) {
    return (
      <div style={{ maxWidth: 1000, margin: '0 auto', padding: 24 }}>
        <div style={{
          padding: 24,
          background: '#fed7d7',
          borderRadius: 12,
          color: '#c53030',
          textAlign: 'center'
        }}>
          <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>
            {error || 'Session not found'}
          </h3>
          <button
            onClick={() => navigate('/p2p-games/challenges')}
            style={{
              marginTop: 16,
              padding: '10px 20px',
              background: '#667eea',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            Back to Challenges
          </button>
        </div>
      </div>
    );
  }

  const isPlayer1 = currentUserId === session.player1Id;
  const opponentReady = isPlayer1 ? session.player2Ready : session.player1Ready;
  const bothReady = session.player1Ready && session.player2Ready;

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: 24 }}>
      {/* Header */}
      <div style={{ marginBottom: 32 }}>
        <button
          onClick={() => navigate('/p2p-games/challenges')}
          style={{
            padding: '8px 16px',
            background: 'transparent',
            color: '#667eea',
            border: '1px solid #667eea',
            borderRadius: 8,
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginBottom: 16
          }}
        >
          <FaArrowLeft />
          Back
        </button>

        <div style={{ textAlign: 'center' }}>
          <div style={{
            display: 'inline-flex',
            padding: '12px 24px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: 12,
            color: 'white',
            alignItems: 'center',
            gap: 12,
            marginBottom: 16
          }}>
            <FaGamepad style={{ fontSize: 24 }} />
            <h1 style={{ fontSize: 28, fontWeight: 700 }}>
              Game Lobby
            </h1>
          </div>
          <p style={{ fontSize: 16, color: '#718096' }}>
            {session.gameName}
          </p>
        </div>
      </div>

      {/* Countdown Overlay */}
      {countdown !== null && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            textAlign: 'center',
            color: 'white'
          }}>
            <div style={{
              fontSize: 120,
              fontWeight: 700,
              marginBottom: 16,
              animation: 'pulse 1s ease-in-out'
            }}>
              {countdown}
            </div>
            <div style={{ fontSize: 24, fontWeight: 600 }}>
              Game Starting...
            </div>
          </div>
        </div>
      )}

      {/* Players Section */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr auto 1fr',
        gap: 24,
        marginBottom: 32,
        alignItems: 'center'
      }}>
        {/* Player 1 */}
        <div style={{
          background: 'white',
          borderRadius: 16,
          padding: 24,
          border: isPlayer1 ? '3px solid #667eea' : '1px solid #e2e8f0',
          boxShadow: isPlayer1 ? '0 4px 12px rgba(102,126,234,0.2)' : 'none'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: 32,
              fontWeight: 700,
              margin: '0 auto 16px'
            }}>
              {session.player1Username.charAt(0).toUpperCase()}
            </div>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: '#1a202c', marginBottom: 8 }}>
              {session.player1Username}
              {isPlayer1 && <span style={{ color: '#667eea', fontSize: 14 }}> (You)</span>}
            </h3>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '6px 12px',
              background: session.player1Ready ? '#c6f6d5' : '#fed7d7',
              color: session.player1Ready ? '#22543d' : '#c53030',
              borderRadius: 8,
              fontSize: 14,
              fontWeight: 600
            }}>
              {session.player1Ready ? <FaCheckCircle /> : <FaCircle />}
              {session.player1Ready ? 'Ready' : 'Not Ready'}
            </div>
          </div>
        </div>

        {/* VS Badge */}
        <div style={{
          width: 60,
          height: 60,
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontSize: 20,
          fontWeight: 700,
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
        }}>
          VS
        </div>

        {/* Player 2 */}
        <div style={{
          background: 'white',
          borderRadius: 16,
          padding: 24,
          border: !isPlayer1 ? '3px solid #667eea' : '1px solid #e2e8f0',
          boxShadow: !isPlayer1 ? '0 4px 12px rgba(102,126,234,0.2)' : 'none'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: 32,
              fontWeight: 700,
              margin: '0 auto 16px'
            }}>
              {session.player2Username.charAt(0).toUpperCase()}
            </div>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: '#1a202c', marginBottom: 8 }}>
              {session.player2Username}
              {!isPlayer1 && <span style={{ color: '#667eea', fontSize: 14 }}> (You)</span>}
            </h3>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '6px 12px',
              background: session.player2Ready ? '#c6f6d5' : '#fed7d7',
              color: session.player2Ready ? '#22543d' : '#c53030',
              borderRadius: 8,
              fontSize: 14,
              fontWeight: 600
            }}>
              {session.player2Ready ? <FaCheckCircle /> : <FaCircle />}
              {session.player2Ready ? 'Ready' : 'Not Ready'}
            </div>
          </div>
        </div>
      </div>

      {/* Prize Pool */}
      <div style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        borderRadius: 16,
        padding: 24,
        color: 'white',
        marginBottom: 32,
        textAlign: 'center'
      }}>
        <div style={{ fontSize: 14, opacity: 0.9, marginBottom: 8 }}>
          Prize Pool
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
          <FaTrophy style={{ fontSize: 32 }} />
          <div style={{ fontSize: 48, fontWeight: 700 }}>
            €{session.payoutAmount.toFixed(2)}
          </div>
        </div>
        <div style={{ fontSize: 12, opacity: 0.8, marginTop: 8 }}>
          Platform fee: €{session.platformFee.toFixed(2)}
        </div>
      </div>

      {/* Ready Button */}
      {!isReady && (
        <button
          onClick={handleReady}
          disabled={bothReady}
          style={{
            width: '100%',
            padding: '16px 24px',
            background: bothReady ? '#cbd5e0' : '#48bb78',
            color: 'white',
            border: 'none',
            borderRadius: 12,
            fontSize: 16,
            fontWeight: 700,
            cursor: bothReady ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 12,
            marginBottom: 16
          }}
        >
          <FaCheckCircle />
          I'm Ready!
        </button>
      )}

      {/* Waiting Message */}
      {isReady && !bothReady && (
        <div style={{
          padding: 20,
          background: '#e6fffa',
          border: '2px solid #81e6d9',
          borderRadius: 12,
          textAlign: 'center',
          marginBottom: 16
        }}>
          <div style={{ fontSize: 16, fontWeight: 600, color: '#234e52', marginBottom: 8 }}>
            Waiting for opponent...
          </div>
          <div style={{ fontSize: 14, color: '#2c7a7b' }}>
            The game will start automatically when both players are ready
          </div>
        </div>
      )}

      {/* Cancel Button */}
      <button
        onClick={handleCancel}
        style={{
          width: '100%',
          padding: '12px 20px',
          background: '#fed7d7',
          color: '#c53030',
          border: 'none',
          borderRadius: 12,
          fontSize: 14,
          fontWeight: 600,
          cursor: 'pointer'
        }}
      >
        Leave Lobby
      </button>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes pulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.1); opacity: 0.8; }
        }
      `}</style>
    </div>
  );
};

export default GameLobby;

